import React, { useEffect, useState } from "react";
import Tabs from "./Tabs";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import "../styles/app.css";

// Base URL
const base_url = "https://rylsky-api.unbug.in/main/";
const version = "v1";

// Get token from cookies
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

// Get token from query param, localStorage or cookie (in that order)
function getAccessToken() {
  const urlParams = new URLSearchParams(window.location.search);
  const queryToken = urlParams.get("access_token");
  if (queryToken && queryToken.trim() !== "") return queryToken;

  const localToken = localStorage.getItem("access_token");
  if (localToken && localToken.trim() !== "") return localToken;

  const cookieToken = getCookie("access_token");
  if (cookieToken && cookieToken.trim() !== "") return cookieToken;

  return null;
}

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [offers, setOffers] = useState([]);
  const [resources, setResources] = useState([]);
  const [copied, setCopied] = useState(false);
  const [errorType, setErrorType] = useState(null); // null | "server" | "data_validation" | "access_denied"
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(true);

  const checkDeviceType = () => {
    const width = window.innerWidth;
    if (width > 991) {
      setIsMobileOrTablet(false);
      setErrorType("access_denied");
    }
  };

  const copyCode = () => {
    const text = user?.referral_code;
    if (!text) return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        })
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      alert("Copy failed. Please copy manually.");
    }
  };

  const fetchDashboardData = async () => {
    const token = getAccessToken();

    if (!token) {
      setErrorType("access_denied");
      return;
    }

    try {
      const res = await fetch(`${base_url}${version}/royalskyclub-web/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "authorization": "Bearer " + token
        },
        mode: "cors"
      });

      const data = await res.json();

      if (data.response_code === 200) {
        const response = data.response;
        setUser(response.user);
        setOffers(response.offer_wall);
        setResources(response.resources);
      } else if (data.response_code === 401 || data.response_code === 403) {
        setErrorType("access_denied");
      } else if (data.response_code === 412) {
        setErrorType("data_validation");
      } else {
        setErrorType("server");
      }
    } catch (err) {
      console.error("API Error:", err);
      setErrorType("server");
    }
  };

  useEffect(() => {
    checkDeviceType();
    if (isMobileOrTablet) fetchDashboardData();
  }, [isMobileOrTablet]);

  if (errorType) return <ErrorScreen type={errorType} />;
  if (!user) return <LoadingScreen />;

  return (
    <div className="container">
      <header className="user-info">
        <div className="user-name">Hello {user.name} 👋</div>
        <div className="referral" onClick={copyCode}>
          {user.referral_code}
          <span className="copy-tooltip">
            {copied ? "Copied!" : "Click to Copy"}
          </span>
        </div>
      </header>

      <p className="user-intro">
        <b>Welcome to your Royal Sky Club dashboard!</b><br /><br />
        Here you can explore all the latest referral offers and access resources to help you promote and share. Use the referral code displayed above to invite others and earn rewards. Let's grow together! 🚀
      </p>

      <Tabs referralCode={user.referral_code} offers={offers} resources={resources} />
    </div>
  );
}